import React, { useMemo } from 'react';
import { View, Text } from '../lib/react-native-compat';
import { SelectBox } from './billingLaborMap';

// JDE → rate sheet equipment tier map for the Billing tab's Rates & Rules
// view. JDE stamps every owned-fleet (52100) and rental (52200) row with an
// equipment classification tier code (TK-3/4T, EX-24MT-SB, …) that doesn't
// line up with the names on the VoltaGrid Schedule of Equipment Rates, so
// this card maps each tier seen on the job's equipment cost to a rate sheet
// equipment row. Mapped tiers bill hours × the sheet rate (day rates assume
// the provisions' single 10-hour shift, so they bill at rate ÷ 10 per
// hour); unmapped fleet tiers rebill at cost + the Equipment % fallback
// (12%). Fleet hours are the TE equipment timecards; rental hours are the
// unit's HeavyJob card time (rental vouchers only carry a quantity — see
// billingRentalUsage), so a rental tier (tagged `rental` by billingPricing)
// maps and bills the same way, and an unmapped one bills nothing (No Rate)
// until it's mapped. Unmapped rows get an auto-suggested match; a saved
// mapping always wins.
//
// Storage (same Forecast_User_Data tab 'Billing' rows as the rate schedule):
//   fieldName 'eqmap' rowId 'em_<hash(tier code)>'
//     Value = the rate sheet equipment ROW ID ('' = explicitly unmapped —
//     sheet names aren't unique ("Trailer - Water" twice), ids are),
//     Notes = { j: <tier code>, d?: <tier description> }.
// Rendering and save/dirty state live in billingRates.js — this module owns
// the matching logic and the card UI.

// djb2 — same hash billingRateSchedule.js uses for default row ids.
const hash = (s) => {
    let h = 5381;
    for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
    return h.toString(36);
};

// Fold a tier description / sheet name for matching: lowercase, split
// letter↔digit boundaries (110HP → 110 hp), keep < and > (they're the only
// difference between the two skidsteer tiers), drop "to" so "161 to 210 hp"
// meets "161-210 HP", collapse the rest.
export const normTier = (s) => String(s || '')
    .toLowerCase()
    .replace(/([0-9])([a-z])/g, '$1 $2')
    .replace(/([a-z])([0-9])/g, '$1 $2')
    .replace(/[^a-z0-9<>]+/g, ' ')
    .split(' ')
    .filter((w) => w && w !== 'to')
    .join(' ');

export const equipMapRowId = (tier) => 'em_' + hash(normTier(tier));

// JDE tier code → tier description, for tiers that reach the frontend
// without one (rental rows only carry the code; the PCV snapshot has no
// rental description column). The full JDE equipment tier catalog (526
// codes, "Tiers w descriptions.xlsx", 2026-09) — display + suggestion seed
// only, every mapping is editable. A description on the cost row itself
// still wins over this dictionary.
const TIER_DESCR = {
    '4-GAS-MON': '4 Gas Monitor',
    'AC-<10HP': 'Air Compressors - <10 HP',
    'AC-1250CFM': 'Air Compressors-1250 CFM',
    'AC-1600CFM': 'Air Compressors - 1600 CFM',
    'AC-185CFM': 'Air Compressors - 185 CFM',
    'AC-250CFM': 'Air Compressors - 250 CFM',
    'AC-300CFM': 'Air Compressors - 300CFM Hi-Pr',
    'AC-350CFM': 'Air Compressors - 350 CFM',
    'AC-375CFM': 'Air Compressors - 375 CFM',
    'AC-400CFM': 'Air Compressors - 400CFM Hi-Pr',
    'AC-600CFM': 'Air Compressors - 600 CFM',
    'AC-750CFM': 'Air Compressors-750 CFM',
    'AC-900CFM': 'Air Compressors - 900 CFM',
    'AD-30T': 'ADT - 30 Ton',
    'AD-40T': 'ADT - 40 Ton',
    'AIR-SAW': 'Air Saw (less blades)',
    'AT-FL': 'Attachment- ForkLifts',
    'AT-HMR-12K': 'Attachment - Hammer 10-12K',
    'AT-HMR-6K': 'Attachment - Hammer 5-6K',
    'AT-HMR-8K': 'Attachment - Hammer 7-8K',
    'AT-PB-LRG': 'Attachment - Pad Bucket Large',
    'AT-PB-MED': 'Attachment - Pad Bucket Med',
    'ATV-2WD': 'ATV - 2wd Gas Golf Cart',
    'ATV-4WD': 'ATV - 4 Wheel',
    'AT-VX-10T': 'Attachment - Vacuworx 10 Ton',
    'AT-VX-16T': 'Attachment - Vacuworx 16 Ton',
    'AT-VX-25T': 'Attachment - Vacuworx 25 Ton',
    'BB-MC-42': 'Bev Band w/Motorzd Cwl 38"-42"',
    'BB-MC-48': 'Bev Band w/Motorzd Cwl 43"-48"',
    'BH-ATTACH': 'Attachment - Backhoe',
    'BH-ATT-HMR': 'Attachment - Backhoe Hammer',
    'BH-RB-TIRE': 'Rubber Tire Backhoe',
    'BM-02-04': 'Beveling Machine-2" to 4"',
    'BM-04-08': 'Beveling Machine - 4 to 8',
    'BM-06-12': 'Beveling Machine-6" to 12"',
    'BM-14-26': 'Beveling Machine-14" to 26"',
    'BM-16-30': 'Bending Machine 16"-30"',
    'BM-22-36': 'Bending Machine - 22-36 In',
    'BM-28-36': 'Beveling Machine-28" to 36"',
    'BM-6-20': 'Bending Machine - 6-20 In',
    'BU-10-19': 'Bus - 10-19 Passenger',
    'BU-20-29': 'Bus-20-29 Passenger',
    'BU-30-39': 'Bus - 30-39 Passenger',
    'BU-40-49': 'Bus - 40-49 Passenger',
    'CB-30X36MS': 'Directional Drill - 30x36 MS',
    'CB-AT-36-C': 'Attachment - 36 In Drill Head',
    'CB-CRDL-48': 'Boring Machine - Cradle 48 In',
    'CB-CRDL-60': 'Boring Machine - Cradle 60 In',
    'CB-SLCK-48': 'Boring Machine - Slick 48 In',
    'CB-TRCK-48': 'Boring Machine - Track 48 In',
    'CH-<6': 'Chipper-< 6"',
    'CH-06-10': 'Chipper-6" to 10"',
    'CH-10-12': 'Chipper-10" to 12"',
    'CH-SAW': 'ChainSaw',
    'CO-EVAP-CO': 'Port-A-Cool Evaporative Cooler',
    'CPT-WB': 'Compactor-Walk Behind',
    'CR-100RT': 'Crane-91-100 Ton Rough Terrain',
    'CR-100T': 'Crane - 86-100 Ton',
    'CR-101-110': 'Crane - 101-110 Ton',
    'CR-110BC': 'Crane-110 Ton Boom Crawler',
    'CR-130RT': 'Crane-101-130 Ton Rough Terr',
    'CR-130TC': 'Crane-130 Ton Telescop Crawler',
    'CR-150BC': 'Crane-150 Ton Boom Crawler',
    'CR-150RT': 'Crane-131-150 Ton Rough Terr',
    'CR-15-17RT': 'Crane - 15-17 Ton Rough Terr',
    'CR-17-34': 'Crane, 17-34 Ton',
    'CR-18-21RT': 'Crane - 18-21 Ton Rough Terr',
    'CR-200BC': 'Crane-200 Ton Boom Crawler',
    'CR-22-25RT': 'Crane - 22-25 Ton Rough Terr',
    'CR-230BC': 'Crane-230 Ton Boom Crawler',
    'CR-25-30RT': 'Crane - 25-30 Ton Rough Terrai',
    'CR-26-28RT': 'Crane-26-28 Ton Rough Terrain',
    'CR-275BC': 'Crane-275 Ton Boom Crawler',
    'CR-29-39RT': 'Crane-29-39 Ton Rough Terrain',
    'CR-35-45': 'Crane, 35-45 Ton',
    'CR-40-49RT': 'Crane-40-49 Ton Rough Terrain',
    'CR-45-75': 'Crane, 45-75 Ton',
    'CR-50-55RT': 'Crane - 50-55 Ton Rough Terr',
    'CR-50-59RT': 'Crane-50-59 Ton Rough Terrain',
    'CR-550': '550 w/Crane',
    'CR-60-69RT': 'Crane-60-69 Ton Rough Terrain',
    'CR-70-79RT': 'Crane-70-79 Ton Rough Terrain',
    'CR-75-90': 'Crane-75-90 Ton',
    'CR-75TC': 'Crane 75 Ton Telescop Crawler',
    'CR-80-90RT': 'Crane-80-90 Ton Rough Terrain',
    'CT-185CFM': 'GNT Trailer w/ 185 CFM A/Comp',
    'CT-375CFM': 'GNT Trailer w/ 375 CFM A/Comp',
    'CT-TK-185': 'Sandblast/Paint - 3 Ton Truck',
    'CT-TL-185': 'Sandblast/Paint - 185 CFM',
    'CT-TL-375': 'Sandblast/Paint - 375 CFM',
    'CT-TST-CAB': 'Test Cabin/Trailer',
    'CT-VACTRON': 'Vactron - Trailer Mounted',
    'CX-10FT': 'Conex - Container - 10 Ft',
    'CX-20FT': 'Conex - Container - 20 Ft',
    'CX-40FT': 'Conex - Container - 40 Ft',
    'CX-80FT': 'Conex - 80 ft Container',
    'CX-DUMP': 'Dumpster',
    'CX-OFFICE': 'Office Trailer',
    'DD-1.1M': 'Directional Drill - 1.1M Lbs',
    'DD-100K': 'Directional Drill - 100K Lbs',
    'DD-10K': 'Directional Drill - 10K Lbs',
    'DD-20K': 'Directional Drill - 20K Lbs',
    'DD-220K': 'Directional Drill - 220K Lbs',
    'DD-24K': 'Directional Drill - 24K Lbs',
    'DD-28K': 'Directional Drill - 28K Lbs',
    'DD-40K': 'Directional Drill - 40K Lbs',
    'DD-440K': 'Directional Drill - 440K Lbs',
    'DD-625K': 'Directional Drill - 625K Lbs',
    'DY-1550CFM': 'Dryer - 1550 CFM',
    'DY-450CFM': 'Dryer - 450 CFM',
    'DZ-110': 'Dozer - 86-110 HP',
    'DZ-160': 'Dozer - 111-160 HP',
    'DZ-160-W': 'Dozer - 111-160 HP (Winch)',
    'DZ-210': 'Dozer - 161-210 HP',
    'DZ-210-W': 'Dozer - 161-210 HP (Winch)',
    'DZ-280': 'Dozer - 211-280 HP',
    'DZ-280-W': 'Dozer - 211-280 HP (Winch)',
    'DZ-320': 'Dozer - 281-320 HP',
    'DZ-350': 'Dozer-300 to 350 hp',
    'DZ-350-W': 'Dozer-300 to 350 hp w/ Winch',
    'DZ-85': 'Dozer - 70-85 HP',
    'DZ-85-W': 'Dozer-70-85 HP w/ Winch',
    'DZ-ATTACH': 'Attachment - Dozers',
    'EQ-IE-TEST': 'I&E Test Equipment',
    'EX-06MT-SB': 'Excavator - 2-6 Ton (St Bkt)',
    'EX-10MT-SB': 'Excavator - 7-10 Ton (St Bkt)',
    'EX-14MT-SB': 'Excavator - 11-14 Ton (St Bkt)',
    'EX-19MT-2H': 'Excavator-15-19 Ton (2k Hmr)',
    'EX-19MT-SB': 'Excavator - 15-19 Ton (St Bkt)',
    'EX-19MT-TA': 'Excavator-15-19 Ton (Thumb)',
    'EX-24MT-3H': 'Excavator - 20-24 Ton (3K Hmr)',
    'EX-24MT-5H': 'Excavator - 20-24 Ton (5K Hmr)',
    'EX-24MT-SB': 'Excavator - 20-24 Ton (St Bkt)',
    'EX-24MT-TA': 'Excavator - 20-24 Ton (Thumb)',
    'EX-24MT-VP': 'Excavator-20-24 Ton (Vac Lift)',
    'EX-24MT-ZR': 'Excavator - 20-24 Ton (ZR, SB)',
    'EX-29MT-6H': 'Excavator - 25-29 Ton (6K Hmr)',
    'EX-29MT-AB': 'Excavator-25-29 Ton (ALLU Bkt)',
    'EX-29MT-PB': 'Excavator-25-29 Ton (Pad Bkt)',
    'EX-29MT-SB': 'Excavator - 25-29 Ton (St Bkt)',
    'EX-29MT-TA': 'Excavator - 25-29 Ton (Thumb)',
    'EX-29MT-VP': 'Excavator-25-29 Ton (Vac Lift)',
    'EX-30-35MT': 'Excavator, 30-35 MT',
    'EX-30MT-7H': 'Excavator - 30 Ton (7K Hmr)',
    'EX-30MT-PB': 'Excavator - 30 Ton (Pad Bkt)',
    'EX-30MT-SB': 'Excavator - 30 Ton (St Bkt)',
    'EX-30MT-TA': 'Excavator - 30 Ton (Thumb)',
    'EX-30MT-VP': 'Excavator - 30 Ton (Vac Lift)',
    'EX-35MT-7H': 'Excavator - 35 Ton (7K Hmr)',
    'EX-35MT-PB': 'Excavator - 35 Ton (Pad Bkt)',
    'EX-35MT-SB': 'Excavator - 35 Ton (St Bkt)',
    'EX-35MT-TA': 'Excavator - 35 Ton (Thumb)',
    'EX-35MT-VP': 'Excavator - 35 Ton (Vac Lift)',
    'EX-36MT-7H': 'Excavator-30-36 Ton (7k Hmr)',
    'EX-36MT-AB': 'Excavator-30-36 Ton (ALLU Bkt)',
    'EX-36MT-DS': 'Excavator-30-36 Ton (DH Sys)',
    'EX-36MT-PB': 'Excavator-30-36 Ton (Pad Bkt)',
    'EX-36MT-SB': 'Excavator-30-36 Ton (St Bkt)',
    'EX-36MT-TA': 'Excavator-30-36 Ton (Thumb)',
    'EX-36MT-VP': 'Excavator-30-36 Ton (Vac Lift)',
    'EX-3745-SB': 'Excavator-37-45 Ton (St Bkt)',
    'EX-45MT-SB': 'Excavator - 45 Ton (St Bkt)',
    'EX-45MT-TA': 'Excavator-37-45 Ton (Thumb)',
    'EX-45MT-VP': 'Excavator-37-45 Ton (Vac Lift)',
    'EX-45MT-XH': 'Excavator-37-45 Ton (10k Hmr)',
    'EX-4650-SB': 'Excavator-46-50 Ton (St Bkt)',
    'EX-50MT-SB': 'Excavator - 50 Ton (St Bkt)',
    'EX-50MT-VP': 'Excavator-46-50 Ton (Vac Lift)',
    'EX-55MT-SB': 'Excavator-50-55 Ton (St Bkt)',
    'EX-55MT-VP': 'Excavator-50-55 Ton (Vac Lift)',
    'EX-55MT-XH': 'Excavator-50-55 Ton (10k Hmr)',
    'EX-75MT-SB': 'Excavator-65-75 Ton (St Bkt)',
    'EX-75MT-VP': 'Excavator-65-75 Ton (Vac Lift)',
    'EX-75T-12H': 'Excavator-65-75 Ton (12k Hmr)',
    'EX-90MT-SB': 'Excavator-80-90 Ton (St Bkt)',
    'EX-90MT-VP': 'Excavator-80-90 Ton (Vac Lift)',
    'EX-90T-15H': 'Excavator-80-90 Ton (15k Hmr)',
    'EX-ATTACH': 'Attachment - Excavators',
    'FL-<2.5YD': 'Front End Loader-<2.5yd',
    'FL-10-12K': 'Fork Lift 10k-12k lbs capacity',
    'FL-2.5YD': 'Front End Loader - 2.5 Yd',
    'FL-3.0YD': 'Front End Loader - 3 Yd',
    'FL-4.0YD': 'Front End Loader - 4 Yd',
    'FL-4.5YD': 'Front End Loader - 4.5 Yd',
    'FL-5.0YD': 'Front End Loader - 5 Yd',
    'FL-5.5YD': 'Front End Loader-5.5yd',
    'FL-5-8K': 'Fork Lift 5k-8k lbs capacity',
    'FL-6.0YD': 'Front End Loader-6yd',
    'FL-6.5YD': 'Front End Loader-6.5yd',
    'FL-8-12KJ': 'Forklift-Tele-8k-12k lb(Jib)',
    'FL-RT-<8K': 'Forklift - Rough Terr - <8K',
    'FL-RT-812K': 'Forklift - Rough Terr - 8-12K',
    'FL-TH-<8K': 'Forklift - Telehandler - <8K',
    'FL-TH-10K': 'Forklift - Telehandler - 10K',
    'FL-TH-15K': 'Fork Lift: 12,000-15,000 lbs.',
    'FL-TH-812K': 'Forklift - Telehandler - 8-12K',
    'FL-TH-8K': 'Forklift - Telehandler - 8k',
    'FM-4-12': 'Fusion Machine - 4-12 In',
    'FP-V-6IN': 'Fill Pipe-6" Victaulic',
    'FP-W-6IN': 'Fill Pipe-6" Welded',
    'FS-SEQ-6-8': 'Swaging Equip(FlexSteel)-6"-8"',
    'FT-110HP': 'Farm Tractor - 110HP',
    'FT-135HP': 'Farm Tractor - 135HP',
    'FT-20HP': 'Farm Tractor - 20HP',
    'FT-40HP': 'Farm Tractor - 40HP',
    'FT-70HP': 'Farm Tractor - 70HP',
    'FT-ATTACH': 'Attachment - Farm Tractors',
    'GB-36X60': '36x60 Gang Box',
    'GN-<2KW': 'Generator - 1.5 KW',
    'GN-<5KW': 'Generator - <5.0 KW',
    'GN-100KW': 'Generator-100 KW',
    'GN-100KW+': 'Generator - >100 KW',
    'GN-10KW': 'Generator-7.5 to 10 KW',
    'GN-125KW': 'GENERATOR -125 KW',
    'GN-20KW': 'Generator - 20 KW',
    'GN-26KW': 'GENERATOR -26 KW',
    'GN-25KVA': 'Generator - 25kva',
    'GN-2KW': 'Generator - 2.0 KW',
    'GN-35KW': 'Generator - 35 KW',
    'GN-49KW': 'Generator - 30.0-49.0 KW',
    'GN-50KW': 'Generator-50 KW',
    'GN-55KW': 'GENERATOR -55 KW',
    'GN-60KW': 'Generator - 60 KW',
    'GN-7.5KW': 'Generator - 5.0-7.5 KW',
    'GN-70KW': 'Generator - 70 KW',
    'GN-7-10KW': 'Generator - 7.5 to 10 KW',
    'GN-78KW': 'Generator - 78 KW',
    'GN-7KW': 'GENERATOR-7KW',
    'GN-LP-7KW': 'Light Plant - 6.0 - 7.5 KW',
    'HN-HTA-41X': 'Tube Hone',
    'HOL-DET': 'Holiday Detector',
    'HS-35KW': 'Heating System - 35 KW',
    'HS-ACCESS': 'Heating System - Accessory',
    'JK-HMR': 'Jack Hammer',
    'LD-ATTACH': 'Attachment - Loaders',
    'MAT-4X16X6': 'Mats-4\' x 16\' x 6"',
    'MAT-8X16X4': 'Mats-8\' x 16\' x 4"',
    'MC-1200GPM': 'Mud Cleaning Trailer - 1200GPM',
    'MC-1500GPM': 'Mud Cleaning Trailer - 1500GPM',
    'MC-200GPM': 'Mud Cleaning Trailer - 200 GPM',
    'MC-300GPM': 'Mud Cleaning Trailer - 300 GPM',
    'MC-400GPM': 'MUD Cleaning Trailer - 400 GPM',
    'MC-600GPM': 'Mud Cleaning Trailer - 600 GPM',
    'ME-60HP-TL': 'Boat - 60 HP Motor - Trailer',
    'ME-8FT-BRM': 'Broom - Power Sweeper - 8 Ft',
    'ME-AR-HAM': 'Air Rock Hammer',
    'ME-CUT-60A': 'Plasma Cutter - 60 Amp',
    'ME-LIB-42': 'Lower-In Belts - 36-42 Pipe',
    'ME-LIB-48': 'Lower-In Belts - 43-48 Pipe',
    'ME-LIC-20': 'Lower-In Cradle - 6-20 Pipe',
    'ME-LIC-24': 'Lower-In Cradle - 12-24 Pipe',
    'ME-LIC-36': 'Lower-In Cradle - 24-36 Pipe',
    'ME-LIC-48': 'Lower-In Cradle - 37-48 Pipe',
    'ME-LIC-60': 'Lower-In Cradle - 50-60 Pipe',
    'ME-MISC': 'Misc',
    'ME-PCMP-18': 'Pipe Clamps 10-18 In',
    'ME-PCMP-26': 'Pipe Clamps 20-26 In',
    'ME-PCMP-36': 'Pipe Clamps 30-36 In',
    'ME-PCMP-42': 'Pipe Clamps 38-42 In',
    'ME-PCMP-48': 'Pipe Clamps 43-48 In',
    'ME-PIGT-24': 'Pig Trap - 24 In',
    'ME-PIGT-30': 'Pig Trap - 30 In',
    'ME-SB-EQP': 'Sandblasting Equipment',
    'ME-SC-COW': 'Satcom Cow Unit (AT&T Cell)',
    'ME-SC-TL': 'Satcom Unit (Trailer/Fly Away)',
    'ME-SM-TOOL': 'Small Tools',
    'ME-SWAG-26': 'Swaging Equip (Flex) - 2-6 In',
    'ME-TH-12': 'Test Head - 12 In',
    'ME-TH-1620': 'Test Head - 16-20 In',
    'ME-TH-24': 'Test Head - 24 In',
    'ME-TH-30': 'Test Head - 30 In',
    'ME-TH-36': 'Test Head - 36 In',
    'ME-WM-200A': 'Welding Machine - 200 Amp',
    'ME-WM-300A': 'Welding Machine - 300 Amp',
    'ME-WM-800A': 'Welding Machine - 800 Amp',
    'MG-145HP': 'Motor Grader - 125-145 HP',
    'MG-179HP': 'Motor Grader-146 to 179 hp',
    'MG-180HP': 'Motor Grader - 146-180 HP',
    'MG-212HP': 'Motor Grader - 180-212 HP',
    'MG-248HP': 'Motor Grader - 213-248 HP',
    'MG-300HP': 'Motor Grader - 250-300 HP',
    'ML-AT-85': 'Manlift All Terrain 85\'',
    'ML-BL-44FT': 'Man Lift - 30-44 Ft Boom',
    'ML-BL-45FT': 'Man Lift - 45 Ft Boom',
    'ML-BL-59FT': 'Man lift-45\'-59\' Boom Lift',
    'ML-BL-65FT': 'Man Lift - 60-65 Ft Boom',
    'ML-BL-74FT': 'Man lift-60\'-74\' Boom Lift',
    'ML-BL-80FT': 'Man lift - 80\' Boom Lift',
    'ML-BL-85FT': 'Man Lift - 85 Ft Boom',
    'ML-L-125FT': 'Man lift-125\' Lift',
    'ML-L-135FT': 'Man lift - 135\' Lift',
    'ML-L-45FT': 'Manlifts - 45\' Lift',
    'ML-L-65FT': 'Man Lift-60\'-65\' Lift',
    'ML-SL-39FT': 'Man Lift - 25-39 Ft Scissor',
    'ML-SL-54FT': 'Man lift-40\'-54\' Scissor Lift',
    'ML-T-80FT': 'MANLIFT-80\' W/TRAILER',
    'ML-TOW': 'Manlift - Towable',
    'MOW-BH': 'Brush Hog Mower',
    'MOW-P': 'Lawnmower-Push',
    'MOW-WE': 'Weedeater',
    'MT-MATS': 'Mats',
    'PC-14-20IN': 'Pipe Clamps (14" to 20")',
    'PC-22-36IN': 'Pipe Clamps (22" to 36")',
    'PC-2-6IN': 'Pipe Clamps (2" to 6")',
    'PC-8-12IN': 'Pipe Clamps (8" to 12")',
    'PFE-10-18': 'POLY FUSION EQUIPMENT 10"-18"',
    'PFE-2-4': 'POLY FUSION EQUIPMENT 2"-4"',
    'PFE-4-8': 'POLY FUSION EQUIPMENT 4"-8"',
    'PFE-6-12': 'POLY FUSION EQUIPMENT 6"-12"',
    'PFM->18IN': 'Poly Fusion Machine (>18")',
    'PFM-6-18': 'Poly Fusion Machine (6"-18")',
    'P-FSAFS': 'Poly/FlexSteel-A-Frame Set',
    'PH-GV-6IN': 'POWER HEAD W/6" GROOVER',
    'PH-GV-8IN': 'POWER HEAD W/8" GROOVER',
    'PL-100K-SB': 'Pipelayer - 100K Lbs Sideboom',
    'PL-140K-SB': 'Pipelayer - 140K Lbs Sideboom',
    'PL-160K-SB': 'Pipelayer - 160K Lbs Sideboom',
    'PL-200K-SB': 'Pipelayer - 200K Lbs Sideboom',
    'PL-20K-SB': 'Pipelayer - 20K Lbs Sideboom',
    'PL-215K-SB': 'Pipelayer - 215K Lbs Sideboom',
    'PL-225K-SB': 'Pipelayer - 225K Lbs Sideboom',
    'PL-40K-SB': 'Pipelayer - 40K Lbs Sideboom',
    'PL-60K-SB': 'Pipelayer - 60K Lbs Sideboom',
    'PL-80K-SB': 'Pipelayer - 80K Lbs Sideboom',
    'PL-SB-10K': 'Pipelayer-10,000 lb Sideboom',
    'PN-<10K': 'Crawler - LGP - <10K Lbs Cap',
    'PN-20K': 'Crawler - LGP - 10-20K Lbs Cap',
    'PN-30K': 'Crawler - LGP - 21-30K Lbs Cap',
    'PN-45K': 'Crawler - LGP - 31-45K Lbs Cap',
    'PN-WS-TRAC': 'Crawler - Welding Support',
    'PP-GSD6X18': 'Guillotine Saw Pneu D 6"-18"',
    'PP-PT': 'Porta Pony Pipe Threader',
    'PR-REC': 'Pressure Recorder',
    'PR-TEMP-R': 'Pressure/Temperature Recorder',
    'PT-12IN': 'Pig Trap-12 Inch',
    'PT-16-20IN': 'Pig Trap-16 to 20 Inch',
    'PT-36IN': 'Pig Trap-36 Inch',
    'PT-8-10IN': 'Pig Trap-8 to 10 Inch',
    'PT-PWRD': 'Pipe Threader - Powered',
    'PTV-42IN': 'Pig Trap and Valve-42"',
    'PTV-48IN': 'Pig Trap and Valve-48"',
    'PU-CP-2': 'Centrifugal Pump - 2 In',
    'PU-CP-3': 'Centrifugal Pump - 3 In',
    'PU-CP-4': 'Centrifugal Pump - 4 In',
    'PU-CP-6': 'Centrifugal Pump - 6 In',
    'PU-CP-6X3': 'Centrifugal Pump - 6x3',
    'PU-DP-4IN': 'Ditch Pump-4"',
    'PU-DP-6IN': 'Ditch Pump-6"',
    'PU-DPP-2IN': 'Diaphragm Pump-2"',
    'PU-FP-54J': 'Fill Pump - 6x4 54J 800/150',
    'PU-FP-66B': 'Fill Pump - 6x4 66B 1500/150',
    'PU-FP8X6-4': 'Fill Pump-8x6-4stgTMC1000/1000',
    'PU-FP-DMD': 'Fill Pump - 8x6 - DMD 2500/250',
    'PU-HP-10': 'Hydraulic Pump - 10x10',
    'PU-LH-4X4': 'Pump - Low Head 4x4',
    'PU-LHP-6X6': 'Low head Pump-6 Inch x 6 Inch',
    'PU-LHP-8X8': 'Low head Pump-8 Inch x 8 Inch',
    'PU-PP-SVHP': 'Pneumatic Pump - Sm Vol Hi-Pr',
    'PU-PP-TEST': 'Pneumatic Test Pump (Small)',
    'PU-PS-200': 'Pressure Pump - Large - 200 HP',
    'PU-PS-20HP': 'Pressure Pump - Small - 20 HP',
    'PU-PS-400': 'Pressure Pump - XL - >400 HP',
    'PU-PS-TPX': 'Pressure Pump - Medium Triplex',
    'PU-WP-2IN': '2" Water pump',
    'PU-WP-4IN': 'Pump-Water-4 inch',
    'PU-WTP-2-3': '2-3" Water/Trash Pump',
    'PW-1GUN-TL': 'Pressure Washer - 1 Gun, Heat',
    'PW-2GUN-TL': 'Pressure Washer - 2 Gun, Heat',
    'PW-3GUN-TL': 'Pressure Washer - 3 Gun, Heat',
    'PW-4GUN-TL': 'Pressure Washer - 4 Gun, Heat',
    'PW-CART-MT': 'Pressure Washer - Portable',
    'RC-550HP': 'Reclaimer - 501-550 HP',
    'RM-RID-MOW': 'Lawnmower - Riding',
    'RP-10X12': 'Road Plate - 10\' x 12\'',
    'SB-EQUIP': 'Sandblasting Equipment',
    'SC-DD-PF': 'Compactor - Dbl Drum Pad Foot',
    'SC-SPFR-54': 'Compactor - 54 In Smt/Pad Foot',
    'SC-SPFR-66': 'Compactor - 66 In Smt/Pad Foot',
    'SC-SPFR-84': 'Compactor - 84 In Smt/Pad Foot',
    'SC-WB-24': 'Compactor - 24 In Walk Behind',
    'SKIDS': 'Skids',
    'SP-B-RIG': 'Spreader Bar (Rigging etc)',
    'SR-23YD': 'Scraper - 23 Yd',
    'SS-<8K': 'Skidsteer - <8K Lb',
    'SS->8K': 'Skidsteer - > 8K Lb',
    'SS-ATTACH': 'Attachment - Skidsteers',
    'SS-ML': 'Skid Steer Mini Loader',
    'ST-SM-TOOL': 'Sm-Tool',
    'SUV-ALL': 'SUV - All Sizes',
    'SWT-12K': 'Standing Water Tank - 12K Gal',
    'TC-36IN': 'Trencher-36" depth',
    'TH<6"': 'Threader - <6"',
    'TH-42IN': 'Test Head-42 inch',
    'TH-4-6IN': 'Test Head-4 to 6 Inch',
    'TH-48IN': 'Test Head-48 inch',
    'TH-8-10IN': 'Test Head-8 to 10 Inch',
    'TK-1.5-DRA': 'Truck-1 1/2T Roust w/Autocrane',
    'TK-1.5T-BK': 'Truck - 1 1/2 Ton (Bucket)',
    'TK-1.5T-DB': 'Truck - 1 1/2 Ton w/ Dump Bed',
    'TK-1.5T-DW': 'Truck - 1 1/2 Ton (Dual Rear)',
    'TK-1.5T-KV': 'Truck - 1 1/2 Ton w/ KUV Body',
    'TK-1.5T-MR': 'Truck - 1 1/2 Ton Mechanic Rig',
    'TK-1/2T': 'Truck - 1/2 Ton',
    'TK-10T-HL2': 'Truck - 10 Ton Haul - Tandem',
    'TK-10T-HL3': 'Truck - 10 Ton Haul - Triple',
    'TK-10T-HO3': 'Truck - 10 Ton HotOil - Triple',
    'TK-10T-HVC': 'Truck - 10 Ton w/ Hydrovac',
    'TK-12Y-DMP': 'Truck - 12 Yd Tandem Axle Dump',
    'TK-14Y-DMP': 'Truck - 14 Yd Tandem Axle Dump',
    'TK-1T-DUMP': 'Truck - 1 Ton w/ Dump Bed',
    'TK-1T-DW': 'Truck - 1 Ton (Dual Rear)',
    'TK-1T-DWR': 'Truck - 1 Ton Dual Roust.',
    'TK-1T-DWRA': 'Truck-1 Ton Roust w/Autocrane',
    'TK-1T-KUV': 'Truck - 1 Ton w/ KUV Body',
    'TK-1T-MR': 'Truck - 1 Ton Mechanic Rig',
    'TK-1T-SW': 'Truck - 1 Ton (Single Rear)',
    'TK-1T-WELD': 'Truck - 1 Ton (Weld Rig)',
    'TK-22Y-DMP': 'Truck - 22 Yd Tandem Axle Dump',
    'TK-2-3T-FB': 'Truck - 2-3 Ton Flatbed',
    'TK-2-3T-MR': 'Truck - 2-3 Ton Mechanic Rig',
    'TK-2-3T-SB': 'Truck - 2-3 Ton Stake Bed',
    'TK-2T-MR': 'Truck - 2 Ton Mechanic Rig',
    'TK-3/4T': 'Truck - 3/4 Ton',
    'TK-3/4T-T': 'Truck - Pickup w/Tools',
    'TK-3-5T-GN': 'Truck - 3-5 Ton Gang',
    'TK-80BBLVC': 'Truck - 80 Bbl Vac Truck',
    'TK-8T-UTIL': 'Truck - 8 Ton Utility',
    'TK-8Y-DMP': 'Truck - 8 Yd Single Axle Dump',
    'TK-BOOM-15': 'Boom Truck-10-15 Ton',
    'TK-BOOM-20': 'Boom Truck - 16 - 20 Ton',
    'TK-BOOM-25': 'Boom Truck-21-25 Ton',
    'TK-BOOM-30': 'Boom Truck - 26-30 Ton',
    'TK-BOOM-35': 'Boom Truck-31-35 Ton',
    'TK-BOOM-40': 'Boom Truck-36-40 Ton',
    'TK-BOOM-49': 'Boom Truck-40-49 Ton',
    'TK-BOOM-55': 'Boom Truck - 50-55 Ton',
    'TK-BOOM-60': 'Boom Truck - 60 Ton',
    'TK-BOOM-80': 'Boom Truck - 80 Ton',
    'TK-CLASS1': 'Truck Class 1(Ford F150 or eq)',
    'TK-CLASS2': 'Truck Class 2-3(Ford F250-F350',
    'TK-CLASS2C': 'Truck Class 2-3 w/Tool & Crane',
    'TK-CLASS2T': 'Truck Class 2-3 w/Bed & Tools',
    'TK-CLASS4': 'Truck Class 4-5(Ford F450-550)',
    'TK-CLASS4C': 'Truck Class 4-5 w/Tool & Crane',
    'TK-CLASS4T': 'Truck Class 4-5 w/Bed & Tools',
    'TK-CO/SP': 'Truck-Op/Straw Pickup',
    'TK-DIG-47': 'Truck - 47 Ft Digger',
    'TK-DRILL': 'Drilling Rig',
    'TK-FL-2.5K': 'Truck - 2.5K Gallon Fuel',
    'TK-FL-4K': 'Truck - 4K Gallon Fuel Tank',
    'TK-FL-LUBE': 'Truck - Fuel & Lube',
    'TK-GTAW': 'Truck Weld w/Eq Cons(GTAW)',
    'TK-HS-3K': 'Truck - 3K Gallon Hydroseeder',
    'TK-KN-BOOM': 'Truck - Knuckle Boom',
    'TK-LB-55': 'Truck - 55 Ft Line Bucket',
    'TK-SMAW': 'Truck Weld w/Eq Cons(SMAW)',
    'TK-TST-COM': 'Test Truck Combo Fill Test',
    'TK-VAC': 'Truck - Vacuum',
    'TK-WT-2.5K': 'Truck - 2.5K Gallon Water Tank',
    'TK-WT-2K': 'Truck - 2K Gallon Water Tank',
    'TK-WT-3K': 'Truck - 3,000 Gallon Water Tan',
    'TK-WT-4K': 'Truck - 4K Gallon Water Tank',
    'TK-WT-8K': 'Truck - 8,000 Gallon Water Tan',
    'TL-130BBLV': 'Trailer - 130 Bbl Vac',
    'TL-36FT': 'TRAILER 36\'',
    'TL-40FT': '40\' Pipe Trailer',
    'TL-5WHL-PS': 'Trailer - 5th Wheel Pipe Stng',
    'TL-BBLP-80': 'Trailer - 80 Bbl Pup',
    'TL-BD-23Y': 'Trailer - 23.5 Yd Belly Dump',
    'TL-BP-PIPE': 'Trailer - Pipe - Bumper Pull',
    'TL-BPU': 'Trailer - Bumper Pull Utility',
    'TL-BRT-AIR': 'Trailer - Breathing Air',
    'TL-CARGO': 'Trailer - Cargo',
    'TL-CCU-16': 'Trailer - 16 Ft Cooling Combo',
    'TL-DDF': 'Trailer - Drop Deck Float',
    'TL-EBBQ-40': 'Trailer - 40 Ft Encl. BBQ',
    'TL-ED-38': 'Trailer - 38 Ft End Dump',
    'TL-ET-1224': 'Trailer - 12-24 Ft Encl. Tool',
    'TL-FBT': 'Flat Bed Trailer',
    'TL-FIBER': 'Trailer - Fiber',
    'TL-FIRE-PV': 'Trailer - Fire Prevention',
    'TL-FLEXSTL': 'Trailer - Flexsteel',
    'TL-FLOAT': 'Trailer - Float',
    'TL-FL-SA': 'TRAILER -FLOAT SINGLE AXLE',
    'TL-FL-TA': 'TRAILER -FLOAT TANDUM AXLE',
    'TL-GN-2AXL': 'Trailer - Gooseneck Tandem',
    'TL-GN30-35': 'Trailer-Gooseneck 30\' to 35\'',
    'TL-GND-HTR': 'Trailer - Ground Heater',
    'TL-HAUL-48': 'Trailer - 48 Ft Equip Hauler',
    'TL-HL-TANK': 'Trailer - Tank Hauling',
    'TL-HYD-POL': 'Trailer - Hydraulic Poly',
    'TL-LB-4050': 'Trailer - 40-50 Ton Lowboy',
    'TL-LBFA-55': 'Trailer - 55 Ton Lowboy Flip',
    'TL-LBFA-60': 'Trailer - 60 Ton Lowboy Flip',
    'TL-OSCT-16': 'Trailer - 16 Ft Open Side Cool',
    'TL-POLE-67': 'Trailer - 67’ Pole',
    'TL-POLY': 'Trailer - Poly',
    'TL-RO-2C': 'Trailer - Roll Off - Double',
    'TL-RO-DDC': 'Trailer - Roll Off - Drop Deck',
    'TL-RO-SC': 'Trailer - Roll Off - Single',
    'TL-SS-PP': 'Trailer - Single Spool Poly',
    'TL-TRASH': 'Trailer - Trash',
    'TL-UFB-40': 'TRAILER-40\' UTILITY FLATBED',
    'TL-W<500GA': 'Trailer Water < 500 Gallon',
    'TL-W-1.6K': 'Trailer Water 1,000-1,600 Gal',
    'TL-W-500GA': 'Trailer Water 300-500 Gallon',
    'TL-WT-500G': 'Trailer - 500 Gal Water Tank',
    'TL-WT-8K': 'Trailer - 8K Gallon Water Tank',
    'TM-HW': 'Telematics Hardware',
    'TR-<100HP': 'Trencher - <100 HP',
    'TR-225HP': 'Trencher-185 to 225 hp',
    'TR-275HP': 'Trencher - 226-275 HP',
    'TR-325HP': 'Trencher - 276-325 HP',
    'TR-400HP': 'Trencher-326 to 400 hp',
    'TR-450HP': 'Trencher - 400-450 HP',
    'TR-4-DEPTH': 'Trencher - 4 Ft Depth',
    'TR-550HP': 'Trencher - 500-550 HP',
    'TR-RO-4D1': 'Trench-Ride On 48" deep 12"cut',
    'TV-25FT': 'Tool Van-25\'',
    'TV-SP-25FT': 'Tool Van-25\'-Sm Pipeline',
    'TV-TL-53FT': 'ToolVan-40\'-53\' Trlr w/o tool',
    'TV-TOOLS': 'Tool Van w/Tools',
    'TW-HYD': 'Torque Wrench - Hydraulic',
    'VB-TL-4053': 'Tool Van - 40-53 Ft Trailer',
    'VCT-SM': 'Vactron - Skid Mounted',
    'WR-2X200A': 'Tack Rig - 2x200 Amp Weld',
    'WR-4X350A': 'Tack Rig - 4x350 Amp Weld',
    'WR-TR2X200': 'TackRig w/2x200Amp Wld Machine',
    'WS-SS': 'Soft Shack',
    'WS-WD': 'Weld Support - Weld Deck',
    // Codes on the VoltaGrid rate buildup workbook (EQUIP - SW, 2026-08-31)
    // that the 2026-09 JDE catalog export doesn't carry — descriptions come
    // from the workbook's Equipment Tier Description column.
    'X00-WELD': 'Welding Rig',
    'TL-STR': 'Stringing Trailer',
    'AT-HMR-3K': '2024 NPK GH4 3K HAMMER',
    'ML-AB<50FT': "Manlift <= 50' Articulate Boom",
    'ML-AB-65FT': 'Manlift 50-65  Articulate Boom',
    'ML-AB-85FT': "Manlift 65-85' Articulate Boom",
    'SS-ATT-PB': 'PickupBroomSkidsteerAttchmnt',
    'SS-ATT-AGR': 'SkidsteerAugerAttachment w/Bit',
    'PL-WSST': 'Pipelayer Weld Sup Shack Tract',
    'FM-8-24': '2025 Tracstar Fusion Machine',
    'EQ-SWFS-68': 'Swag Equipment FlexSteel 6-8"',
    'DIG-PR-G': 'Digital Pressure Guage',
    'CH-REC': 'Chart Recorder',
    'FT-40-6BH': "FmTractor-40 HP-w/6' BrushHog",
    'FT-40-8BH': "FmTractor-40 HP-w/8' BrushHog",
    'FT-40-8D': "FmTractor-40 HP-w/8' Disc",
    'FT-70-8BH': "FmTractor-70 HP-w/8' BrushHog",
    'FT-70-15B': "FmTractor-70 HP-w/15' Batwing",
    'FT-70-8D': "FmTractor-70 HP-w/8' Disc",
    'FT-70-16D': "FmTractor-70 HP-w/16' Disc",
    'FT-110-15B': "FmTractor-110 HP-w/15' Batwing",
    'FT-110-16D': "FmTractor-110 HP-w/16' Disc",
    'CB-HYD-2-4': 'Hydraulic Conduit Bender 2"-4"',
    'RigHeater': 'Rig Heater',
    'ST-PL-LOC': 'Pipeline Locator',
    'BM-38-42': 'Beveling Machine-38" to 42"',
    'BM-43-48': 'Beveling Machine-43" to 48"',
    'BR-PIN': 'Brazing Pin Unit',
    'EX-35MT-LR': 'Excavator-30-36 Ton Long Reach',
    'ATT-MOW': 'Attachment - Mower Deck',
};

// Alias table: folded tier description → rate sheet row, for descriptions
// that don't match a sheet name on folding alone. `no` disambiguates the few
// duplicate sheet names ("Trailer - Water" ×2) by notes substring.
// Suggestions only — every row is editable on the card and a saved mapping
// overrides these. Descriptions with no confident sheet row (GN-78KW,
// 3RD-PARTY, generic attachments, oversized gear the sheet has no tier
// for …) are left out on purpose: unmapped bills cost + the Equipment %
// fallback, which is what the provisions say for gear that isn't on the
// schedule. The commented entries below the first batch were curated off
// the full tier catalog ("Tiers w descriptions.xlsx", 2026-09).
const ALIASES = {
    'forklift telehandler 10 k': { n: 'Forklift - Telehandler - 10k to 12k lb Cap.' },
    'forklift telehandler 8 12 k': { n: 'Forklift - Telehandler - 10k to 12k lb Cap.' },
    'truck 1 ton dual rear': { n: 'Truck - 1 Ton (Double Rear Wheel)' },
    'truck 1 ton dual roust': { n: 'Truck - 1 Ton (Double Rear Wheel)' },
    'truck 1 ton single rear': { n: 'Truck - 1 Ton (Single Rear Wheel)' },
    'truck 1 1 2 ton dual rear': { n: 'Truck - 1 1/2 Ton (Double Rear Wheel)' },
    'truck 1 1 2 ton mechanic rig': { n: 'Truck - 1.5 Ton Mechanic Rig' },
    'truck 1 1 2 t roust w autocrane': { n: 'Truck - 1.5 Ton w/Autocrane' },
    'truck op straw pickup': { n: 'Truck - 3/4 Ton' },
    'truck pickup w tools': { n: 'Truck - 3/4 Ton' },
    'truck weld w eq cons smaw': { n: 'Truck - 1 Ton (Weld Rig) Rod/Stick' },
    'truck 1 ton weld rig': { n: 'Truck - 1 Ton (Weld Rig) Rod/Stick' },
    'truck 10 ton haul triple': { n: 'Truck - 10 Ton Haul - Three Axle' },
    'truck 10 ton haul tandem': { n: 'Truck - 10 Ton Haul - Tandem Axle' },
    'truck 2 k gallon water tank': { n: 'Truck - 2,000 Gallon Water Tanker' },
    'truck 4 k gallon water tank': { n: 'Truck - 4,000 Gallon Water Tanker' },
    'excavator 2 6 ton st bkt': { n: 'Excavator - 2 to 6 Metric Ton (Machine with Standard Bucket)' },
    'excavator 7 10 ton st bkt': { n: 'Excavator - 7 to 10 Metric Ton (Machine with Standard Bucket)' },
    'excavator 11 14 ton st bkt': { n: 'Excavator - 11 to 14 Metric Ton (Machine with Standard Bucket)' },
    'excavator 20 24 ton st bkt': { n: 'Excavator - 20 to 24 Metric Ton (Machine with Standard Bucket)' },
    'excavator 20 24 ton zr sb': { n: 'Excavator - 20 to 24 Metric Ton (Machine with Standard Bucket)' },
    'excavator 25 29 ton st bkt': { n: 'Excavator - 25 to 29 Metric Ton (Machine with Standard Bucket)' },
    'excavator 30 ton st bkt': { n: 'Excavator - 30 to 36 Metric Ton (Machine with Standard Bucket)' },
    'excavator 35 ton st bkt': { n: 'Excavator - 30 to 36 Metric Ton (Machine with Standard Bucket)' },
    'rubber tire backhoe': { n: 'Rubber Tire Backhoe (Machine with Standard Bucket)' },
    'trailer 12 24 ft encl tool': { n: "Trailer - 12' - 24' Enclosed Tool Trailer" },
    'tool van 40 53 ft trailer': { n: "Tool Van - 40' - 53' Trailer" },
    'trailer 55 ton lowboy flip': { n: 'Trailer - 55 Ton Lowboy w/ Flip Axle' },
    'trailer water 300 500 gallon': { n: 'Trailer - Water', no: '300-500' },
    'trailer 16 ft cooling combo': { n: "Trailer - 16' Open Side Cooling Trailer" },
    'trailer gooseneck tandem': { n: 'Trailer - Gooseneck Tandem Axle' },
    'skidsteer > 8 k lb': { n: 'Skid Steer - >8K' },
    'skidsteer < 8 k lb': { n: 'Skid Steer Mini Loader' },
    'dozer 161 210 hp winch': { n: 'Dozer - 161 to 210 hp' },
    'fusion machine 4 12 in': { n: 'Poly Fusion Machine (6" to 10")' },
    'attachment pad bucket large': { n: 'Attachment - Excavator Padding Bucket', no: '30-36' },
    'attachment pad bucket med': { n: 'Attachment - Excavator Padding Bucket', no: '25-29' },
    'attachment hammer 5 6 k': { n: 'Attachment - Excavator 6,500 lb Hammer' },
    'attachment hammer 7 8 k': { n: 'Attachment - Excavator 7,500 lb Hammer' },
    'attachment vacuworx 10 ton': { n: 'Attachment - Vacuum Pipe Lift' },
    'man lift 85 ft boom': { n: "Man lift - 85' Lift - Telescopic" },
    'man lift 60 65 ft boom': { n: "Man lift - 60' - 65' Lift - Telescopic" },
    'man lift 45 ft boom': { n: "Man lift - 50' or less, Telescopic Boom" },
    'manlift towable': { n: "Man lift - 30' - 44' - Articulating Boom Lift" },
    'pipelayer 40 k lbs sideboom': { n: 'Pipelayer - 40,000 lb Sideboom' },
    'pipelayer 100 k lbs sideboom': { n: 'Pipelayer - 100,000 lb Sideboom' },
    'crawler lgp 21 30 k lbs cap': { n: 'Crawler LGP Carrier (Not in Tack Rig Config) - 21k to 30k lbs Cap.' },
    'compactor 84 in smt pad foot': { n: 'Compactor - 84" Smooth/Pad Foot Roller' },
    'compactor 24 in walk behind': { n: 'Compactor - Walk Behind' },
    'pressure temperature recorder': { n: 'Digital Pressure / Temperature Recorder' },
    'pressure washer 1 gun heat': { n: 'Pressure Washer - 1 Gun, Trailer Mounted, Heated w/ Water Tank' },
    'generator 30 0 49 0 kw': { n: 'Generator - 30 KW' },
    'tack rig 4 x 350 amp weld': { n: 'Tack Rig w/ 4 x 350 Amp Welding Machines (including Soft Welding Shack)' },
    'air compressors 400 cfm hi pr': { n: 'Air Compressors - 400 CFM High Pressure' },
    mats: { n: "Mats - 4' x 16' x 6\"" },
    'broom power sweeper 8 ft': { n: 'Broom - 8 ft Power Sweeper' }, // ME-8FT-BRM: Broom - Power Sweeper - 8 Ft
    'truck 8 000 gallon water tan': { n: 'Truck - 8,000 Gallon Water Tanker' }, // TK-WT-8K: Truck - 8,000 Gallon Water Tan
    'truck 1 ton roust w autocrane': { n: 'Truck - 1 Ton w/Autocrane' }, // TK-1T-DWRA: Truck-1 Ton Roust w/Autocrane
    'truck 1 1 2 ton w dump bed': { n: 'Truck - 1 Ton (w/ Dump Bed)' }, // TK-1.5T-DB: Truck - 1 1/2 Ton w/ Dump Bed
    'truck 2 ton mechanic rig': { n: 'Truck - 1.5 Ton Mechanic Rig' }, // TK-2T-MR: Truck - 2 Ton Mechanic Rig
    'truck 2 3 ton mechanic rig': { n: 'Truck - 1.5 Ton Mechanic Rig' }, // TK-2-3T-MR: Truck - 2-3 Ton Mechanic Rig
    'truck 2 3 ton stake bed': { n: 'Truck - 2 - 3 Ton Flatbed' }, // TK-2-3T-SB: Truck - 2-3 Ton Stake Bed
    'truck 2 5 k gallon fuel': { n: 'Truck - 2,500 Gallon Fuel' }, // TK-FL-2.5K: Truck - 2.5K Gallon Fuel
    'lower in cradle 6 20 pipe': { n: 'Lower-in Cradles - 6" - 20" Pipe' }, // ME-LIC-20: Lower-In Cradle - 6-20 Pipe
    'lower in cradle 12 24 pipe': { n: 'Lower-in Cradles - 12" - 24" Pipe' }, // ME-LIC-24: Lower-In Cradle - 12-24 Pipe
    'lower in cradle 24 36 pipe': { n: 'Lower-in Cradles - 24" - 36" Pipe' }, // ME-LIC-36: Lower-In Cradle - 24-36 Pipe
    'lower in cradle 50 60 pipe': { n: 'Lower-in Cradles - 50" - 60" Pipe' }, // ME-LIC-60: Lower-In Cradle - 50-60 Pipe
    'crane 15 17 ton rough terr': { n: 'Crane - 15-17 Ton Rough Terrain' }, // CR-15-17RT: Crane - 15-17 Ton Rough Terr
    'crane 18 21 ton rough terr': { n: 'Crane - 18-21 Ton Rough Terrain' }, // CR-18-21RT: Crane - 18-21 Ton Rough Terr
    'crane 22 25 ton rough terr': { n: 'Crane - 22-25 Ton Rough Terrain' }, // CR-22-25RT: Crane - 22-25 Ton Rough Terr
    'crane 50 55 ton rough terr': { n: 'Crane - 50-59 Ton Rough Terrain' }, // CR-50-55RT: Crane - 50-55 Ton Rough Terr
    'crane 101 110 ton': { n: 'Crane - 101-130 Ton Rough Terrain' }, // CR-101-110: Crane - 101-110 Ton
    'crane 101 130 ton rough terr': { n: 'Crane - 101-130 Ton Rough Terrain' }, // CR-130RT: Crane-101-130 Ton Rough Terr
    'crane 131 150 ton rough terr': { n: 'Crane - 131-150 Ton Rough Terrain' }, // CR-150RT: Crane-131-150 Ton Rough Terr
    'crane 75 ton telescop crawler': { n: 'Crane - 75 Ton Telescoping Crawler' }, // CR-75TC: Crane 75 Ton Telescop Crawler
    'crane 130 ton telescop crawler': { n: 'Crane - 130 Ton Telescoping Crawler' }, // CR-130TC: Crane-130 Ton Telescop Crawler
    'motor grader 146 179 hp': { n: 'Motor Grader - 146 to 180 hp' }, // MG-179HP: Motor Grader-146 to 179 hp
    'air compressors <10 hp': { n: 'Air Compressors - < 10 HP' }, // AC-<10HP: Air Compressors - <10 HP
    'air compressors 300 cfm hi pr': { n: 'Air Compressors - 300 CFM High Pressure' }, // AC-300CFM: Air Compressors - 300CFM Hi-Pr
    'air compressors 900 cfm': { n: 'Air Compressors - 900 CFM High Pressure' }, // AC-900CFM: Air Compressors - 900 CFM
    'air compressors 1600 cfm': { n: 'Air Compressors - 1600 CFM High Pressure' }, // AC-1600CFM: Air Compressors - 1600 CFM
    'generator 2 0 kw': { n: 'Generator - <5.0 KW' }, // GN-2KW: Generator - 2.0 KW
    'generator 1 5 kw': { n: 'Generator - <5.0 KW' }, // GN-<2KW: Generator - 1.5 KW
    'generator 7 kw': { n: 'Generator - 5.0 to 7.5 KW' }, // GN-7KW: GENERATOR-7KW
    'generator 35 kw': { n: 'Generator - 30 KW' }, // GN-35KW: Generator - 35 KW
    'dozer 70 85 hp w winch': { n: 'Dozer - 70 to 85 hp' }, // DZ-85-W: Dozer-70-85 HP w/ Winch
    'dozer 111 160 hp winch': { n: 'Dozer - 111 to 160 hp' }, // DZ-160-W: Dozer - 111-160 HP (Winch)
    'dozer 211 280 hp winch': { n: 'Dozer - 211 to 280 hp' }, // DZ-280-W: Dozer - 211-280 HP (Winch)
    'dozer 300 350 hp w winch': { n: 'Dozer - 300 to 350 hp' }, // DZ-350-W: Dozer-300 to 350 hp w/ Winch
    'pipelayer 20 k lbs sideboom': { n: 'Pipelayer - 20,000 lb Sideboom' }, // PL-20K-SB: Pipelayer - 20K Lbs Sideboom
    'pipelayer 60 k lbs sideboom': { n: 'Pipelayer - 60,000 lb Sideboom' }, // PL-60K-SB: Pipelayer - 60K Lbs Sideboom
    'pipelayer 80 k lbs sideboom': { n: 'Pipelayer - 80,000 lb Sideboom' }, // PL-80K-SB: Pipelayer - 80K Lbs Sideboom
    'pipelayer 140 k lbs sideboom': { n: 'Pipelayer - 140,000 lb Sideboom' }, // PL-140K-SB: Pipelayer - 140K Lbs Sideboom
    'crawler lgp 31 45 k lbs cap': { n: 'Crawler LGP Carrier (Not in Tack Rig Config) - 31k to 45k lbs Cap.' }, // PN-45K: Crawler - LGP - 31-45K Lbs Cap
    'crawler lgp <10 k lbs cap': { n: 'Crawler LGP Carrier (Not in Tack Rig Config) - <10k lbs Cap.' }, // PN-<10K: Crawler - LGP - <10K Lbs Cap
    'compactor 54 in smt pad foot': { n: 'Compactor - 54" Smooth/Pad Foot Roller' }, // SC-SPFR-54: Compactor - 54 In Smt/Pad Foot
    'compactor 66 in smt pad foot': { n: 'Compactor - 66" Smooth/Pad Foot Roller' }, // SC-SPFR-66: Compactor - 66 In Smt/Pad Foot
    'forklift telehandler 8 k': { n: 'Forklift - Telehandler - 8k to 9k lb Cap.' }, // FL-TH-8K: Forklift - Telehandler - 8k
    'forklift tele 8 k 12 k lb jib': { n: 'Forklift - Telehandler - 10k to 12k lb Cap.' }, // FL-8-12KJ: Forklift-Tele-8k-12k lb(Jib)
    'bending machine 6 20 in': { n: 'Bending Machine - 6" - 20"' }, // BM-6-20: Bending Machine - 6-20 In
    'bending machine 22 36 in': { n: 'Bending Machine - 22" - 36"' }, // BM-22-36: Bending Machine - 22-36 In
    'bev band w motorzd cwl 38 42': { n: 'Beveling Band w/ Motorized Crawler (38" to 42")' }, // BB-MC-42: Bev Band w/Motorzd Cwl 38"-42"
    'bev band w motorzd cwl 43 48': { n: 'Beveling Band w/ Motorized Crawler (43" to 48")' }, // BB-MC-48: Bev Band w/Motorzd Cwl 43"-48"
    'poly fusion equipment 2 4': { n: 'Poly Fusion Machine (2" to 4")' }, // PFE-2-4: POLY FUSION EQUIPMENT 2"-4"
    'poly fusion equipment 10 18': { n: 'Poly Fusion Machine (12" to 18")' }, // PFE-10-18: POLY FUSION EQUIPMENT 10"-18"
    'centrifugal pump 2 in': { n: 'Centrifugal Pump - 2"' }, // PU-CP-2: Centrifugal Pump - 2 In
    'centrifugal pump 3 in': { n: 'Centrifugal Pump - 3"' }, // PU-CP-3: Centrifugal Pump - 3 In
    'centrifugal pump 4 in': { n: 'Centrifugal Pump - 4"' }, // PU-CP-4: Centrifugal Pump - 4 In
    'centrifugal pump 6 in': { n: 'Centrifugal Pump - 6"' }, // PU-CP-6: Centrifugal Pump - 6 In
    '2 water pump': { n: 'Centrifugal Pump - 2"' }, // PU-WP-2IN: 2" Water pump
    'pump water 4 inch': { n: 'Centrifugal Pump - 4"' }, // PU-WP-4IN: Pump-Water-4 inch
    'hydraulic pump 10 x 10': { n: 'Hydraulic Pump' }, // PU-HP-10: Hydraulic Pump - 10x10
    'pressure recorder': { n: 'Digital Pressure / Temperature Recorder' }, // PR-REC: Pressure Recorder
    'test cabin trailer': { n: 'Hydro Test Trailer' }, // CT-TST-CAB: Test Cabin/Trailer
    'guillotine saw pneu d 6 18': { n: 'Guillotine Saw, 6"-18"' }, // PP-GSD6X18: Guillotine Saw Pneu D 6"-18"
    'pressure washer portable': { n: 'Pressure Washer - Portable Cart Mounted' }, // PW-CART-MT: Pressure Washer - Portable
    'swaging equip flexsteel 6 8': { n: 'Swaging Equipment (FlexSteel) - 6"-8"' }, // FS-SEQ-6-8: Swaging Equip(FlexSteel)-6"-8"
    'swaging equip flex 2 6 in': { n: 'Swaging Equipment (FlexSteel) - 2"-6"' }, // ME-SWAG-26: Swaging Equip (Flex) - 2-6 In
    'attachment hammer 10 12 k': { n: 'Attachment - Excavator 10,000 lb Hammer' }, // AT-HMR-12K: Attachment - Hammer 10-12K
    'excavator 30 ton thumb': { n: 'Excavator - 30 to 36 Metric Ton (Machine with Standard Bucket)' }, // EX-30MT-TA: Excavator - 30 Ton (Thumb)
    'tack rig 2 x 200 amp weld': { n: 'Tack Rig w/ 2 x 200 Amp Welding Machines' }, // WR-2X200A: Tack Rig - 2x200 Amp Weld
    'tackrig w 2 x 200 amp wld machine': { n: 'Tack Rig w/ 2 x 200 Amp Welding Machines' }, // WR-TR2X200: TackRig w/2x200Amp Wld Machine
    'trailer float tandum axle': { n: 'Trailer - Float' }, // TL-FL-TA: TRAILER -FLOAT TANDUM AXLE
    'trailer float single axle': { n: 'Trailer - Float' }, // TL-FL-SA: TRAILER -FLOAT SINGLE AXLE
    'trailer 60 ton lowboy flip': { n: 'Trailer - 55 Ton Lowboy w/ Flip Axle' }, // TL-LBFA-60: Trailer - 60 Ton Lowboy Flip
    'trailer 5 th wheel pipe stng': { n: 'Trailer - 5th Wheel Pipe Stringing Rollagon' }, // TL-5WHL-PS: Trailer - 5th Wheel Pipe Stng
    'trailer hydraulic poly': { n: 'Trailer - Poly' }, // TL-HYD-POL: Trailer - Hydraulic Poly
    'trailer single spool poly': { n: 'Trailer - Poly' }, // TL-SS-PP: Trailer - Single Spool Poly
    'trailer gooseneck 30 35': { n: 'Trailer - Gooseneck Tandem Axle' }, // TL-GN30-35: Trailer-Gooseneck 30' to 35'
};

// ── The bid's own tier assignments ──
// Authoritative JDE tier → rate sheet row map straight off the "EQUIP - SW"
// sheet of VoltaGrid - Rate Buildup - Working File 2026 (Eleah, 2026-08-31):
// every equipment row there carries the rate sheet name ("Strike Equipment
// Description") next to its JDE tier code, so nothing is guessed for these
// codes — this is the mapping the bid was priced on. `null` = the workbook
// marks the tier No Bid: deliberately off the schedule, bills cost + the
// Equipment % fallback, and the null blocks the name heuristics above from
// suggesting anything. Codes the workbook doesn't carry (winch dozers,
// GTAW weld rigs, …) still fall through to the fold/alias heuristics.
// Suggestions only — a saved mapping always wins and every row is editable.
// Where the workbook lists one code against several sheet rows the pick is
// noted inline.
const BID_TIER_ROWS = {
    'TK-1/2T': { n: 'Truck - 1/2 Ton' },
    'TK-3/4T': { n: 'Truck - 3/4 Ton' },
    'TK-1T-SW': { n: 'Truck - 1 Ton (Single Rear Wheel)' },
    'TK-1T-DW': { n: 'Truck - 1 Ton (Double Rear Wheel)' },
    'TK-1T-DWRA': { n: 'Truck - 1 Ton w/Autocrane' },
    'TK-1T-MR': { n: 'Truck - 1 Ton Mechanic Rig' },
    'TK-1.5T-MR': { n: 'Truck - 1.5 Ton Mechanic Rig' },
    'TK-1.5T-DB': { n: 'Truck - 1 Ton (w/ Dump Bed)' },
    'X00-WELD': { n: 'Truck - 1 Ton (Weld Rig) Rod/Stick' }, // workbook lists Rod/Stick + TIG — Rod/Stick is the common rig
    'TK-1.5T-DW': { n: 'Truck - 1 1/2 Ton (Double Rear Wheel)' },
    'TK-2-3T-FB': { n: 'Truck - 2 - 3 Ton Flatbed' },
    'TK-1.5-DRA': { n: 'Truck - 1.5 Ton w/Autocrane' },
    'TK-10T-HL2': { n: 'Truck - 10 Ton Haul - Tandem Axle' },
    'TK-10T-HL3': { n: 'Truck - 10 Ton Haul - Three Axle' },
    'TK-FL-2.5K': { n: 'Truck - 2,500 Gallon Fuel' },
    'TK-WT-2K': { n: 'Truck - 2,000 Gallon Water Tanker' },
    'TK-WT-4K': { n: 'Truck - 4,000 Gallon Water Tanker' },
    'TL-WT-8K': { n: 'Truck - 8,000 Gallon Water Tanker' },
    'TK-12Y-DMP': { n: 'Truck - 12 Yd Tandem Axle Dump' },
    'TK-FL-LUBE': { n: 'Truck - Fuel & Lube' },
    'BU-10-19': { n: 'Bus - 15 Passenger Van' },
    'TL-CCU-16': { n: 'Trailer - 16\' Open Side Cooling Trailer' },
    'TL-LB-4050': { n: 'Trailer - 40 - 50 Ton Lowboy' },
    'TL-HAUL-48': { n: 'Trailer - 40 - 50 Ton Lowboy' },
    'TL-LBFA-55': { n: 'Trailer - 55 Ton Lowboy w/ Flip Axle' },
    'TL-BPU': { n: 'Trailer - Bumper Pull Utility' },
    'TL-BP-PIPE': { n: 'Trailer - Pipe - Bumper Pull' },
    'TL-DDF': { n: 'Trailer - Drop Deck Float' },
    'TL-FLEXSTL': { n: 'Trailer - FlexSteel' },
    'TL-FLOAT': { n: 'Trailer - Float' },
    'TL-STR': { n: 'Trailer - 5th Wheel Pipe Stringing Rollagon' },
    'TL-GN-2AXL': { n: 'Trailer - Gooseneck Tandem Axle' },
    'TL-HYD-POL': { n: 'Trailer - Poly' },
    'TL-W-500GA': { n: 'Trailer - Water', no: '300-500 Gallon capac' },
    'AD-30T': { n: 'ADT - 30 Ton' },
    'AD-40T': { n: 'ADT - 40 Ton' },
    'SR-23YD': { n: 'Scraper - 23 yd' },
    'ATV-4WD': { n: 'ATV - 4 Wheel' },
    'BH-RB-TIRE': { n: 'Rubber Tire Backhoe (Machine with Standard Bucket)' },
    'EX-06MT-SB': { n: 'Excavator - 2 to 6 Metric Ton (Machine with Standard Bucket)' },
    'EX-10MT-SB': { n: 'Excavator - 7 to 10 Metric Ton (Machine with Standard Bucket)' },
    'EX-14MT-SB': { n: 'Excavator - 11 to 14 Metric Ton (Machine with Standard Bucket)' },
    'EX-19MT-SB': { n: 'Excavator - 15 to 19 Metric Ton (Machine with Standard Bucket)' },
    'AT-HMR-3K': { n: 'Attachment - Excavator 3,500 lb Hammer' },
    'AT-HMR-6K': { n: 'Attachment - Excavator 6,500 lb Hammer' },
    'AT-HMR-8K': { n: 'Attachment - Excavator 7,500 lb Hammer' },
    'AT-HMR-12K': { n: 'Attachment - Excavator 10,000 lb Hammer' },
    'EX-24MT-SB': { n: 'Excavator - 20 to 24 Metric Ton (Machine with Standard Bucket)' },
    'EX-24MT-ZR': { n: 'Excavator - 20 to 24 Metric Ton (Machine with Standard Bucket)' },
    'EX-24MT-5H': { n: 'Excavator - 20 to 24 Metric Ton (Machine with Standard Bucket)' },
    'EX-29MT-SB': { n: 'Excavator - 25 to 29 Metric Ton (Machine with Standard Bucket)' },
    'AT-VX-25T': { n: 'Attachment - Excavator Vacuum Pipe Lift', no: '25-29 Metric Ton Exc' },
    'AT-PB-MED': { n: 'Attachment - Excavator Padding Bucket', no: '25-29 Metric Ton Exc' },
    'AT-PB-LRG': { n: 'Attachment - Excavator Padding Bucket', no: '30-36 Metric Ton Exc' },
    'EX-35MT-SB': { n: 'Excavator - 30 to 36 Metric Ton (Machine with Standard Bucket)' },
    'EX-35MT-LR': { n: 'Excavator - 30 to 36 Metric Ton - Long Reach (Machine with Standard Bucket)' },
    'EX-45MT-SB': { n: 'Excavator - 37 to 45 Metric Ton (Machine with Standard Bucket)' },
    'DZ-85': { n: 'Dozer - 70 to 85 hp' },
    'DZ-110': { n: 'Dozer - 86 to 110 hp' },
    'DZ-160': { n: 'Dozer - 111 to 160 hp' },
    'DZ-210': { n: 'Dozer - 161 to 210 hp' },
    'DZ-210-W': { n: 'Dozer - 161 to 210 hp' },
    'DZ-280': { n: 'Dozer - 211 to 280 hp' },
    'DZ-350': { n: 'Dozer - 300 to 350 hp' },
    'MG-145HP': { n: 'Motor Grader - 125 to 145 hp' },
    'MG-180HP': { n: 'Motor Grader - 146 to 180 hp' },
    'MG-212HP': { n: 'Motor Grader - 180 to 212 hp' },
    'MG-248HP': { n: 'Motor Grader - 213 to 248 hp' },
    'MG-300HP': { n: 'Motor Grader - 250 to 300 hp' },
    'SC-WB-24': { n: 'Compactor - Walk Behind' },
    'SC-SPFR-66': { n: 'Compactor - 66" Smooth/Pad Foot Roller' },
    'SC-SPFR-54': { n: 'Compactor - 54" Smooth/Pad Foot Roller' },
    'SC-SPFR-84': { n: 'Compactor - 84" Smooth/Pad Foot Roller' },
    'CR-15-17RT': { n: 'Crane - 15-17 Ton Rough Terrain' },
    'CR-18-21RT': { n: 'Crane - 18-21 Ton Rough Terrain' },
    'CR-22-25RT': { n: 'Crane - 22-25 Ton Rough Terrain' },
    'CR-26-28RT': { n: 'Crane - 26-28 Ton Rough Terrain' },
    'CR-29-39RT': { n: 'Crane - 29-39 Ton Rough Terrain' },
    'CR-40-49RT': { n: 'Crane - 40-49 Ton Rough Terrain' },
    'CR-50-59RT': { n: 'Crane - 50-59 Ton Rough Terrain' },
    'CR-60-69RT': { n: 'Crane - 60-69 Ton Rough Terrain' },
    'CR-70-79RT': { n: 'Crane - 70-79 Ton Rough Terrain' },
    'CR-80-90RT': { n: 'Crane - 80-90 Ton Rough Terrain' },
    'CR-100RT': { n: 'Crane - 91-100 Ton Rough Terrain' },
    'CR-130RT': { n: 'Crane - 101-130 Ton Rough Terrain' },
    'CR-150RT': { n: 'Crane - 131-150 Ton Rough Terrain' },
    'CR-75TC': { n: 'Crane - 75 Ton Telescoping Crawler' },
    'CR-130TC': { n: 'Crane - 130 Ton Telescoping Crawler' },
    'CR-110BC': { n: 'Crane - 110 Ton Boom Crawler' },
    'CR-150BC': { n: 'Crane - 150 Ton Boom Crawler' },
    'FL-RT-<8K': { n: 'Forklift - Rough Terrain (non telehandler) - < 8k lb Cap.' },
    'FL-TH-10K': { n: 'Forklift - Telehandler - 8k to 9k lb Cap.' },
    'FL-TH-812K': { n: 'Forklift - Telehandler - 10k to 12k lb Cap.' },
    'ML-TOW': { n: 'Man lift - 50\' or less, Telescopic Boom' },
    'ML-BL-65FT': { n: 'Man lift - 60\' - 65\' Lift - Telescopic' },
    'ML-BL-85FT': { n: 'Man lift - 85\' Lift - Telescopic' },
    'ML-L-125FT': { n: 'Man lift - 125\' Lift - Telescopic' },
    'ML-L-135FT': { n: 'Man lift - 135\' Lift' },
    'ML-SL-39FT': { n: 'Man lift - 25\' - 39\' Scissor Lift' },
    'ML-SL-54FT': { n: 'Man lift - 40\' - 54\' Scissor Lift' },
    'ML-AB<50FT': { n: 'Man lift - 30\' - 44\' - Articulating Boom Lift' },
    'ML-AB-65FT': { n: 'Man lift - 45\' - 59\' - Articulating Boom Lift' },
    'ML-AB-85FT': { n: 'Man lift - 80\' - Articulating Boom Lift' }, // workbook lists 60-74' + 80' — 85' machine sits in the 80' tier
    'SS-ML': { n: 'Skid Steer Mini Loader' },
    'SS-<8K': { n: 'Skid Steer Mini Loader' },
    'SS->8K': { n: 'Skid Steer - >8K' },
    'SS-ATT-PB': { n: 'Attachment - Broom (for Skid Steer Mini Loader)' },
    'ATT-MOW': { n: 'Attachment - Mower Deck (for Skid Steer Mini Loader)' },
    'SS-ATT-AGR': { n: 'Attachment - Auger w/Bit (for Skid Steer)' },
    'SS-ATTACH': { n: 'Attachment - Mulching Head (for Skid Steer)' },
    'FL-2.5YD': { n: 'Front End Loader - 2.5yd' },
    'FL-3.0YD': { n: 'Front End Loader - 3yd' },
    'FL-4.0YD': { n: 'Front End Loader - 4yd' },
    'FL-4.5YD': { n: 'Front End Loader - 4.5yd' },
    'FL-5.0YD': { n: 'Front End Loader - 5yd' },
    'FL-5.5YD': { n: 'Front End Loader - 5.5yd' },
    'FL-6.0YD': { n: 'Front End Loader - 6yd' },
    'FL-6.5YD': { n: 'Front End Loader - 6.5yd' },
    'PL-SB-10K': { n: 'Pipelayer - 10,000 lb Sideboom' },
    'PL-20K-SB': { n: 'Pipelayer - 20,000 lb Sideboom' },
    'PL-40K-SB': { n: 'Pipelayer - 40,000 lb Sideboom' },
    'PL-60K-SB': { n: 'Pipelayer - 60,000 lb Sideboom' },
    'PL-80K-SB': { n: 'Pipelayer - 80,000 lb Sideboom' },
    'PL-100K-SB': { n: 'Pipelayer - 100,000 lb Sideboom' },
    'PL-140K-SB': { n: 'Pipelayer - 140,000 lb Sideboom' },
    'PN-<10K': { n: 'Crawler LGP Carrier (Not in Tack Rig Config) - <10k lbs Cap.' },
    'PN-20K': { n: 'Crawler LGP Carrier (Not in Tack Rig Config) - 13k-20k lbs Cap.' },
    'PN-30K': { n: 'Crawler LGP Carrier (Not in Tack Rig Config) - 21k to 30k lbs Cap.' },
    'PN-45K': { n: 'Crawler LGP Carrier (Not in Tack Rig Config) - 31k to 45k lbs Cap.' },
    'BM-6-20': { n: 'Bending Machine - 6" - 20"' },
    'BM-16-30': { n: 'Bending Machine - 16" - 30"' },
    'BM-22-36': { n: 'Bending Machine - 22" - 36"' },
    'ME-LIC-20': { n: 'Lower-in Cradles - 6" - 20" Pipe' },
    'ME-LIC-24': { n: 'Lower-in Cradles - 12" - 24" Pipe' },
    'ME-LIC-36': { n: 'Lower-in Cradles - 24" - 36" Pipe' },
    'ME-LIC-60': { n: 'Lower-in Cradles - 50" - 60" Pipe' },
    'ME-LIB-42': { n: 'Lower-in Belts - 36" - 42" Pipe' },
    'ME-LIB-48': { n: 'Lower-in Belts - 43" - 48" Pipe' },
    'WR-4X350A': { n: 'Welding Support Tractors: LGP Carrier w/ 4 x Welding Machines' },
    'PL-WSST': { n: 'Welding Support Tractors: Pipelayer Welding Support Tractor (a/k/a Shack Tractor)' },
    'WR-TR2X200': { n: 'Tack Rig w/ 2 x 200 Amp Welding Machines' },
    'WS-WD': { n: 'Weld Deck' },
    'FM-4-12': { n: 'Poly Fusion Machine (6" to 10")' },
    'PFM-6-18': { n: 'Poly Fusion Machine (12" to 18")' },
    'PFM->18IN': { n: 'Poly Fusion Machine (18" to 23")' },
    'FM-8-24': { n: 'Poly Fusion Machine (24")' },
    'ME-WM-200A': { n: 'Welding Machine - 200 Amp' },
    'ME-WM-300A': { n: 'Welding Machine - 300 Amp' },
    'P-FSAFS': { n: 'Poly/FlexSteel - A-Frame Set' },
    'ME-SWAG-26': { n: 'Swaging Equipment (FlexSteel) - 2"-6"' },
    'EQ-SWFS-68': { n: 'Swaging Equipment (FlexSteel) - 6"-8"' },
    'ME-CUT-60A': { n: 'Plasma Cutter - 60 Amp' },
    'AC-<10HP': { n: 'Air Compressors - < 10 HP' },
    'AC-185CFM': { n: 'Air Compressors - 185 CFM' },
    'AC-250CFM': { n: 'Air Compressors - 250 CFM' },
    'AC-375CFM': { n: 'Air Compressors - 375 CFM' },
    'AC-300CFM': { n: 'Air Compressors - 300 CFM High Pressure' },
    'AC-400CFM': { n: 'Air Compressors - 400 CFM High Pressure' },
    'AC-900CFM': { n: 'Air Compressors - 900 CFM High Pressure' },
    'AC-1600CFM': { n: 'Air Compressors - 1600 CFM High Pressure' },
    'CT-TL-185': { n: 'Sandblast & Paint Rig - Trailer w/ 185cfm A/Comp, 300lb blast pot, hoses, regular paint pot w/agitate & liner. Painter truck is billed separately.' },
    'CT-TL-375': { n: 'Sandblast & Paint Rig - Trailer w/ 375cfm A/Comp, 600lb blast pot, hoses, regular paint pot w/agitate & liner. Painter truck is billed separately.' },
    'ME-SB-EQP': { n: 'Sandblasting Equipment' },
    'GN-LP-7KW': { n: 'Light Plant - 6.0 - 7.5 KW' },
    'GN-<5KW': { n: 'Generator - <5.0 KW' },
    'GN-7.5KW': { n: 'Generator - 5.0 to 7.5 KW' },
    'GN-10KW': { n: 'Generator - 7.5 to 10 KW' },
    'GN-25KVA': { n: 'Generator - 25kva' },
    'GN-49KW': { n: 'Generator - 30 KW' },
    'PU-CP-2': { n: 'Centrifugal Pump - 2"' },
    'PU-CP-3': { n: 'Centrifugal Pump - 3"' },
    'PU-CP-4': { n: 'Centrifugal Pump - 4"' },
    'PU-CP-6': { n: 'Centrifugal Pump - 6"' },
    'PU-DPP-2IN': { n: 'Diaphragm Pump - 2"' },
    'PU-DP-4IN': { n: 'Ditch Pump - 4"' },
    'PU-DP-6IN': { n: 'Ditch Pump - 6"' },
    'PU-HP-10': { n: 'Hydraulic Pump' },
    'PR-TEMP-R': { n: 'Digital Pressure / Temperature Recorder' },
    'DIG-PR-G': { n: 'Digital Pressure Gauge' },
    'CH-REC': { n: 'Chart Recorder' },
    'TK-80BBLVC': { n: 'Truck - 80 BBL Vac Truck' },
    'TK-DRILL': { n: 'Drilling Rig' },
    'PU-PS-20HP': { n: 'Hydraulic Coke Pump' },
    'CH-SAW': { n: 'Chain Saw' },
    'CH-<6': { n: 'Chipper - < 6"' },
    'CH-06-10': { n: 'Chipper - 6" to 10"' },
    'CH-10-12': { n: 'Chipper - 10" to 12"' },
    'FT-40HP': { n: 'Farm Tractor - 40 HP' },
    'FT-40-6BH': { n: 'Attachment - 6\' Brush Hog (40 HP)' },
    'FT-40-8BH': { n: 'Attachment - 8\' Brush Hog (40 HP)' },
    'FT-40-8D': { n: 'Attachment - 8\' Disc (40 HP)' },
    'FT-70HP': { n: 'Farm Tractor - 70 HP' },
    'FT-70-8BH': { n: 'Attachment - 8\' Brush Hog (70 HP)' },
    'FT-70-15B': { n: 'Attachment - 15\' Batwing (70 HP)' },
    'FT-70-8D': { n: 'Attachment - 8\' Disc (70 HP)' },
    'FT-70-16D': { n: 'Attachment - 16\' Disc (70 HP)' },
    'FT-110HP': { n: 'Farm Tractor - 110 HP' },
    'FT-110-15B': { n: 'Attachment - 15\' Batwing (110 HP)' },
    'FT-110-16D': { n: 'Attachment - 16\' Disc (110 HP)' },
    'MOW-P': { n: 'Lawnmower - Push' },
    'RM-RID-MOW': { n: 'Lawnmower - Riding' },
    'MOW-WE': { n: 'Weedeater' },
    'CB-HYD-2-4': { n: 'Hydraulic Conduit Bender 2" to 4"' },
    'EQ-IE-TEST': { n: 'Instrumentation Testing' },
    'SP-B-RIG': { n: 'Spreader Bar (Rigging etc)' },
    'HS-35KW': { n: 'Heating System - 35kw' },
    'ME-8FT-BRM': { n: 'Broom - 8 ft Power Sweeper' },
    'PW-CART-MT': { n: 'Pressure Washer - Portable Cart Mounted' },
    'PW-1GUN-TL': { n: 'Pressure Washer - 1 Gun, Trailer Mounted, Heated w/ Water Tank' },
    'CO-EVAP-CO': { n: 'Port-a-Cool Evaporative Cooler' },
    'RP-10X12': { n: 'Road Plate - 10\' x 12\'' },
    'SKIDS': { n: 'Skids' },
    'MAT-4X16X6': { n: 'Mats - 4\' x 16\' x 6"' },
    'MAT-8X16X4': { n: 'Mats - 8\' x 16\' x 4"' },
    'GB-36X60': { n: '36x60 Gang Box' },
    'PP-GSD6X18': { n: 'Guillotine Saw, 6"-18"' },
    'RigHeater': { n: 'Rig Heater' },
    'TL-ET-1224': { n: 'Trailer - 12\' - 24\' Enclosed Tool Trailer' },
    'VB-TL-4053': { n: 'Tool Van - 40\' - 53\' Trailer' }, // workbook lists with- and without-tools rows — with tools is the fleet standard
    'ST-PL-LOC': { n: 'Pipeline Locator (Metrotech)' },
    'BB-MC-42': { n: 'Beveling Band w/ Motorized Crawler (38" to 42")' },
    'BB-MC-48': { n: 'Beveling Band w/ Motorized Crawler (43" to 48")' },
    'BM-02-04': { n: 'Beveling Machine - 2" to 4"' },
    'BM-06-12': { n: 'Beveling Machine - 6" to 12"' },
    'BM-14-26': { n: 'Beveling Machine - 14" to 26"' },
    'BM-28-36': { n: 'Beveling Machine - 28" to 36"' },
    'BM-38-42': { n: 'Beveling Machine - 38" to 42"' },
    'BM-43-48': { n: 'Beveling Machine - 43" to 48"' },
    'PC-2-6IN': { n: 'Pipe Clamps (2" to 6")' },
    'PC-8-12IN': { n: 'Pipe Clamps (8" to 12")' },
    'PC-14-20IN': { n: 'Pipe Clamps (14" to 20")' },
    'PC-22-36IN': { n: 'Pipe Clamps (22" to 36")' },
    'ME-PCMP-42': { n: 'Pipe Clamps (38" to 42")' },
    'ME-PCMP-48': { n: 'Pipe Clamps (43" to 48")' },
    'HOL-DET': { n: 'Holiday Detector' },
    'JK-HMR': { n: 'Jack Hammer' },
    'AIR-SAW': { n: 'Air Saw (less blades)' },
    'TW-HYD': { n: 'Torque Wrench - Hydraulic' },
    'BR-PIN': { n: 'Brazing Pin Unit' },
    '4-GAS-MON': { n: '4 Gas Monitor' },
    'TH<6"': { n: '1224 Rigid Threader 1/2"- 4"' }, // workbook lists all three threaders — the 1224's range is closest to <6"
    'BH-ATTACH': { n: 'Attachment - Backhoe Hammer' }, // workbook code "BH-ATTACH (Cat H75E Hammer)"
    'CT-VACTRON': { n: 'Vactron - Trailer Mounted' }, // workbook code "CT-VACTRON (trailer mounted)"
    'FT-ATTACH': { n: 'Farm Implement' }, // workbook code "FT-ATTACH (Disk, Straw Blower, Seed Drill)"
    // No Bid on the workbook — off the schedule on purpose, cost + Equipment %.
    'CR-200BC': null,
    'CR-230BC': null,
    'CR-275BC': null,
    'EX-30MT-SB': null,
    'FL-10-12K': null,
    'FL-TH-<8K': null,
    'FS-SEQ-6-8': null,
    'TK-1T-DWR': null,
    'TL-POLY': null,
    'TL-SS-PP': null,
};

// Every tier the bid actually includes — the card lists all of these even
// before the tier posts cost on the job, mirroring the labor map's use of
// the full classification catalog.
export const BID_TIER_CODES = Object.keys(BID_TIER_ROWS).filter((c) => BID_TIER_ROWS[c]);

// The workbook's Bid / No Bid column for a tier: 'Bid', 'No Bid', or '' for
// codes the workbook doesn't carry at all. Drives the card's row tags and
// the Excel export's In Bid column.
export const tierBidStatus = (code) => {
    const c = String(code || '').trim();
    if (!Object.prototype.hasOwnProperty.call(BID_TIER_ROWS, c)) return '';
    return BID_TIER_ROWS[c] ? 'Bid' : 'No Bid';
};

export const tierDescr = (code, descr) => String(descr || '').trim() || TIER_DESCR[String(code || '').trim()] || '';

// Resolve an alias/name to one merged-schedule row: exact name match, then
// the notes discriminator when the sheet reuses a name.
const rowByName = (rows, name, no) => {
    const hits = rows.filter((r) => r.name === name);
    if (hits.length <= 1) return hits[0] || null;
    return hits.find((r) => no && String(r.notes || '').includes(no)) || hits[0];
};

// Suggested rate sheet equipment row id for a JDE tier, restricted to rows
// that actually exist on this job's (possibly edited) equipment schedule.
// The bid workbook's own assignment wins outright; the name heuristics only
// run for codes the workbook doesn't carry. '' when there's no confident
// match — better unmapped (cost + 12%) than wrongly rate-billed.
export const suggestEquipRow = (code, descr, equipRows) => {
    const c = String(code || '').trim();
    if (Object.prototype.hasOwnProperty.call(BID_TIER_ROWS, c)) {
        const e = BID_TIER_ROWS[c];
        if (!e) return ''; // No Bid — deliberately off the schedule
        const row = rowByName(equipRows || [], e.n, e.no);
        if (row) return row.id;
        // sheet row renamed or deleted on this job — fall through
    }
    const d = tierDescr(code, descr);
    if (!d) return '';
    const k = normTier(d);
    const exact = (equipRows || []).find((r) => normTier(r.name) === k);
    if (exact) return exact.id;
    const alias = ALIASES[k];
    if (!alias) return '';
    const row = rowByName(equipRows || [], alias.n, alias.no);
    return row ? row.id : '';
};

// Stored mappings out of the job's Billing rows: { <tier code>: <row id> }.
// A row that exists with a blank Value is an explicit "unmapped" (it blocks
// the auto-suggestion); blank Value + blank Notes is the reset no-op.
export const parseEquipMap = (userRows) => {
    const map = {};
    (userRows || []).forEach((x) => {
        if (x?.Forecast_Field_Name !== 'eqmap') return;
        if (!String(x.Notes || '').trim() && !String(x.Forecast_Value || '').trim()) return;
        try {
            const p = JSON.parse(x.Notes);
            if (p && p.j) map[p.j] = String(x.Forecast_Value || '');
        } catch (e) { /* malformed blob — ignore */ }
    });
    return map;
};

const th = { fontSize: 10, fontWeight: '700', color: '#64748b', letterSpacing: 0.4, paddingTop: 6, paddingBottom: 6, paddingRight: 8 };

const fmtHrs = (n) => (Math.round((Number(n) || 0) * 10) / 10).toLocaleString('en-US');

// The card. Draft state (patch) lives in billingRates.js so mapping edits
// ride the page's single Save-changes flow.
//   tiers:  [{ code, descr, hrs }] from the job's 52100/52200 equipment cost
//   equip:  the merged (defaults + overrides) equipment schedule rows
//   stored: parseEquipMap(userRows)
//   patch:  unsaved edits { <tier code>: <row id | ''> }
//   fmtRate: (row) => display string for the row's rate + uom
//   fallback: display string for unmapped tiers ("cost + 12%")
const EquipTierMap = ({ tiers, equip, stored, patch, onChange, fmtRate, fallback, locked }) => {
    // Sheet names repeat ("Trailer - Water" ×2), so options carry a notes
    // hint and select by label → row id.
    const { labels, idByLabel, labelById } = useMemo(() => {
        const counts = {};
        (equip || []).forEach((r) => { counts[r.name] = (counts[r.name] || 0) + 1; });
        const lbls = [];
        const toId = {};
        const toLabel = {};
        (equip || []).forEach((r) => {
            if (!r.name) return;
            let l = counts[r.name] > 1 && r.notes ? `${r.name} · ${String(r.notes).slice(0, 40)}` : r.name;
            while (toId[l] !== undefined) l += ' ';
            lbls.push(l);
            toId[l] = r.id;
            toLabel[r.id] = l;
        });
        return { labels: lbls, idByLabel: toId, labelById: toLabel };
    }, [equip]);

    // Job tiers first, then saved mappings with no job cost, then the rest
    // of the bid's tier list — every tier the bid includes shows up here so
    // the whole mapping is reviewable before cost posts (same shape as the
    // labor map's classification catalog rows).
    const rows = useMemo(() => {
        const inJob = new Set((tiers || []).map((t) => t.code));
        const extras = Object.keys(stored || {}).filter((c) => !inJob.has(c))
            .map((code) => ({ code, descr: '', hrs: 0, extra: true }));
        const seen = new Set([...inJob, ...extras.map((e) => e.code)]);
        const bid = BID_TIER_CODES.filter((c) => !seen.has(c))
            .map((code) => ({ code, descr: '', hrs: 0, bid: true }));
        return [...(tiers || []), ...extras, ...bid];
    }, [tiers, stored]);

    // What a row bills as today — an unsaved edit, then a saved mapping, then
    // the best guess. Shared by the table and the coverage line below it.
    const valueFor = (r) => {
        if (patch[r.code] !== undefined) return patch[r.code];
        if (stored[r.code] !== undefined) return stored[r.code];
        return suggestEquipRow(r.code, r.descr, equip);
    };

    // Rate sheet equipment nothing maps to — matched on row id, since sheet
    // names repeat ("Trailer - Water" x2).
    const gaps = useMemo(() => {
        const hit = new Set();
        rows.forEach((r) => { const v = valueFor(r); if (v) hit.add(v); });
        return (equip || []).filter((r) => r.name && !hit.has(r.id));
        // valueFor is derived from patch/stored, both already in the dep list
    }, [rows, equip, patch, stored]);

    if (!rows.length) {
        return (
            <Text style={{ fontSize: 12, color: '#94a3b8' }}>
                No equipment usage with a JDE tier on this job yet — rows appear here as fleet (52100) usage posts to cost history and rental units log hours in HeavyJob.
            </Text>
        );
    }

    return (
        <View>
            <View style={{ flexDirection: 'row', borderBottomWidth: 1, borderColor: '#e2e8f0' }}>
                <Text style={[th, { width: 40 }]}>#</Text>
                <Text style={[th, { flexGrow: 1 }]}>JDE EQUIPMENT TIER</Text>
                <Text style={[th, { width: 90, textAlign: 'right' }]}>JTD HOURS</Text>
                <Text style={[th, { width: 268, paddingLeft: 12 }]}>RATE SHEET EQUIPMENT</Text>
                <Text style={[th, { width: 110, textAlign: 'right' }]}>BILLS AT</Text>
            </View>
            {rows.map((r, ri) => {
                const value = valueFor(r);
                const row = value ? (equip || []).find((x) => x.id === value) : null;
                const descr = tierDescr(r.code, r.descr);
                return (
                    <View key={r.code} style={{ flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderColor: '#f1f5f9', paddingTop: 3, paddingBottom: 3 }}>
                        <Text style={{ fontSize: 11, color: '#94a3b8', width: 40 }}>{ri + 1}</Text>
                        <View style={{ flexGrow: 1, flexBasis: 0, paddingRight: 8 }}>
                            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                <Text style={{ fontSize: 12, color: '#1e293b' }}>{r.code}</Text>
                                {tierBidStatus(r.code) ? (
                                    <Text style={{
                                        fontSize: 9, fontWeight: '700', color: '#475569', borderWidth: 1, borderColor: '#cbd5e1',
                                        borderRadius: 3, paddingLeft: 4, paddingRight: 4, marginLeft: 6,
                                    }}
                                    >
                                        {tierBidStatus(r.code).toUpperCase()}
                                    </Text>
                                ) : null}
                            </View>
                            {descr ? <Text style={{ fontSize: 10, color: '#94a3b8' }} numberOfLines={1}>{descr}</Text> : null}
                            {r.extra ? (
                                <Text style={{ fontSize: 10, color: '#94a3b8' }}>saved mapping — not on this job's cost</Text>
                            ) : null}
                            {r.bid ? (
                                <Text style={{ fontSize: 10, color: '#94a3b8' }}>in bid — no cost on this job yet</Text>
                            ) : null}
                            {r.rental ? (
                                <Text style={{ fontSize: 10, color: '#94a3b8' }}>
                                    {r.ru ? 'rental — bills its HeavyJob hours' : 'rental — no HeavyJob hours on this job, vouchers bill cost + %'}
                                </Text>
                            ) : null}
                        </View>
                        <Text style={{ fontSize: 12, color: '#64748b', width: 90, textAlign: 'right', paddingRight: 8 }}>
                            {r.extra || r.bid || (r.rental && !r.hrs) ? '—' : fmtHrs(r.hrs)}
                        </Text>
                        <View style={{ width: 268, paddingLeft: 12 }}>
                            <SelectBox
                                value={row ? labelById[row.id] : ''}
                                // No SUGGESTED chip — with the bid workbook seeding
                                // every row it would sit on nearly all of them and
                                // stop meaning anything (same call as the labor map).
                                options={labels}
                                onSelect={(label) => onChange(r.code, label ? idByLabel[label] : '')}
                                disabled={locked}
                            />
                        </View>
                        <Text style={{ fontSize: 12, color: row ? '#1e293b' : r.ru ? '#92400e' : '#94a3b8', width: 110, textAlign: 'right' }}>
                            {row ? fmtRate(row) : r.ru ? 'no rate' : fallback}
                        </Text>
                    </View>
                );
            })}
            <View style={{ marginTop: 8 }}>
                <Text style={{ fontSize: 11, color: '#64748b' }}>
                    {`Rate sheet coverage: ${(equip || []).length - gaps.length} of ${(equip || []).length} equipment rows have a JDE tier mapped to them.`}
                </Text>
                {gaps.length ? (
                    <View style={{ marginTop: 4 }}>
                        <Text style={{ fontSize: 11, fontWeight: '700', color: '#0f172a' }}>
                            {`${gaps.length} unmapped — nothing on this job bills at these rates:`}
                        </Text>
                        <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2, lineHeight: 16 }}>
                            {gaps.map((r) => labelById[r.id] || r.name).join(' · ')}
                        </Text>
                    </View>
                ) : (
                    <Text style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                        Every rate sheet equipment row has at least one JDE tier mapped to it.
                    </Text>
                )}
            </View>
        </View>
    );
};

export default EquipTierMap;
